import { DialogBox } from "@/components/DialogBox/DialogBox";
import { CloseSvg, ExpandedSvg } from "@/components/Icon/Icon";
import { Box, Icon, IconButton, Paper, Theme, Typography, useTheme } from "@mui/material";
import { FC, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

interface ProgressDrawerProps {
  shouldOnCloseShowModal: () => boolean;
  handleCancelAll: () => void;
  hasUploads: boolean;
  children: ReactNode;
}

export const ProgressDrawer: FC<ProgressDrawerProps> = ({ shouldOnCloseShowModal, handleCancelAll, hasUploads, children }) => {
  const theme = useTheme();
  const { t } = useTranslation("cases");
  const [collapsed, setCollapsed] = useState(false);
  const [cancelAllModalOpen, setCancelAllModalOpen] = useState(false);

  if (!hasUploads) {
    return <></>;
  }

  const handleToggleDrawer = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCollapsed((prevState) => !prevState);
  };

  const handleCloseButton = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (shouldOnCloseShowModal()) {
      // Show warning modal
      setCancelAllModalOpen(true);
    } else {
      handleCancelAll();
    }
  };

  return (
    <>
      <Paper
        elevation={2}
        sx={{
          bgColor: theme.palette.background.paper,
          position: "fixed",
          right: "24px",
          bottom: "0px",
          zIndex: "7",
          width: "350px",
          maxHeight: "300px",
          borderRadius: "6px 6px 0px 0px !important",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "0",
          padding: "0",
          border: (theme: Theme) => `1px solid ${theme.outline.main}`,
          ...(collapsed && { maxHeight: "51px" })
        }}
      >
        <Box
          component="div"
          sx={{
            width: "100%",
            padding: "8px 12px",
            boxSizing: "border-box",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
            borderBottom: (theme: Theme) => `1px solid ${theme.outline.main}`,
            marginBottom: "-1px",
            zIndex: "1"
          }}
          onClick={(e: React.SyntheticEvent) => handleToggleDrawer(e)}
        >
          <Typography>{t("title.uploads")}</Typography>
          <Box
            component="div"
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "0.5em"
            }}
          >
            <IconButton size="small" onClick={(e: React.SyntheticEvent) => handleToggleDrawer(e)}>
              <Icon
                style={{
                  textAlign: "center",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  transform: collapsed ? "rotate(180deg)" : "rotate(0deg)"
                }}
              >
                <ExpandedSvg />
              </Icon>
            </IconButton>
            <IconButton size="small" onClick={(e: React.SyntheticEvent) => handleCloseButton(e)}>
              <Icon
                style={{
                  textAlign: "center",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center"
                }}
              >
                <CloseSvg />
              </Icon>
            </IconButton>
          </Box>
        </Box>
        <Box
          component="div"
          sx={{
            width: "100%",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            padding: "0",
            gap: "0",
            height: "252px"
          }}
        >
          {children}
          <Box
            component="div"
            sx={{
              height: "auto",
              flexGrow: "1",
              width: "100%",
              backgroundColor: theme.surface.low,
              borderTop: (theme: Theme) => `1px solid ${theme.outline.main}`
            }}
          />
        </Box>
      </Paper>
      <DialogBox
        title={t("common:label.cancelUploadsModalTitle")}
        args={undefined}
        message={<Typography>{t("common:label.rejectModal")}</Typography>}
        open={cancelAllModalOpen}
        confirmColor="error"
        confirmText={t("common:label.cancelUploadsModalButtonPrimary")}
        declineText={t("common:label.cancelUploadModalButtonSecondary")}
        setOpen={setCancelAllModalOpen}
        onClose={(approve) => {
          if (approve) {
            handleCancelAll();
          }
        }}
        sx={{ ".MuiPaper-root": { width: "100%", margin: "16px" } }}
      />
    </>
  );
};
